<template>
  <BaseCard class="w-full max-w-md relative z-10" :title="cardTitle" :description="cardDescription">
    <form v-if="!isResetMode" class="space-y-5 mt-2" @submit.prevent="submit">
      <BaseInput
        v-model="form.email"
        label="Email"
        type="email"
        placeholder="admin@example.com"
        :error="errors.email"
        required
        autofocus
        @update:modelValue="clearError('email')"
      />
      <BaseInput
        v-model="form.password"
        label="Password"
        type="password"
        placeholder="Enter your password"
        :error="errors.password"
        required
        @update:modelValue="clearError('password')"
      />
      <div class="flex justify-end">
        <button
          type="button"
          class="text-sm text-primary hover:underline"
          @click="openResetMode"
        >
          Forgot password?
        </button>
      </div>
      <div class="flex justify-between gap-3 pt-2">
        <BaseButton variant="ghost" type="button" @click="routeHome">
          <AppIcon name="ArrowLeft" :size="16" />
          Back to home
        </BaseButton>
        <BaseButton variant="primary" type="submit" :loading="isLoading">Sign In</BaseButton>
      </div>
    </form>

    <form v-else class="space-y-5 mt-2" @submit.prevent="submitReset">
      <BaseInput
        v-model="resetEmail"
        label="Email"
        type="email"
        placeholder="Enter your admin email"
        :error="errors.email"
        required
        autofocus
        @update:modelValue="clearError('email')"
      />
      <p class="text-sm text-muted">
        We will send a password reset link to this email if an account exists.
      </p>
      <div class="flex justify-between gap-3 pt-2">
        <BaseButton variant="ghost" type="button" @click="closeResetMode">
          <AppIcon name="ArrowLeft" :size="16" />
          Back to sign in
        </BaseButton>
        <BaseButton variant="primary" type="submit" :loading="isLoading">Send reset link</BaseButton>
      </div>
    </form>
  </BaseCard>
</template>

<script>
import { firebase } from '@/api/firebase'
import { useToast } from '@/composables/useToast'
import BaseCard from '@/components/ui/BaseCard.vue'
import BaseInput from '@/components/ui/BaseInput.vue'
import BaseButton from '@/components/ui/BaseButton.vue'
import AppIcon from '@/components/ui/AppIcon.vue'

export default {
  name: 'SignIn',
  components: { BaseCard, BaseInput, BaseButton, AppIcon },
  data() {
    return {
      isLoading: false,
      isResetMode: false,
      resetEmail: '',
      form: { email: '', password: '' },
      errors: {},
    }
  },
  computed: {
    cardTitle() {
      return this.isResetMode ? 'Reset Password' : 'Admin Sign In'
    },
    cardDescription() {
      return this.isResetMode
        ? 'Enter your email to receive a reset link.'
        : 'Sign in to manage your portfolio.'
    },
  },
  methods: {
    routeHome() {
      this.$router.push('/')
    },
    openResetMode() {
      this.isResetMode = true
      this.resetEmail = this.form.email
      this.errors = {}
    },
    closeResetMode() {
      this.isResetMode = false
      this.errors = {}
    },
    validateEmail(email) {
      return String(email)
        .toLowerCase()
        .match(
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        )
    },
    clearError(field) {
      this.errors[field] = null
    },
    required(form) {
      let isValid = true
      this.errors = {}
      if (!form.email) {
        this.errors.email = 'Required field'
        isValid = false
      } else if (!this.validateEmail(form.email)) {
        this.errors.email = 'Invalid email'
        isValid = false
      }
      if (!form.password) {
        this.errors.password = 'Required field'
        isValid = false
      }
      return isValid
    },
    validateResetEmail() {
      this.errors = {}
      if (!this.resetEmail) {
        this.errors.email = 'Required field'
        return false
      }
      if (!this.validateEmail(this.resetEmail)) {
        this.errors.email = 'Invalid email'
        return false
      }
      return true
    },
    formatAuthError(message) {
      return message?.toString().split('/')[1]?.replace('-', ' ').replace(')', '') || 'Request failed'
    },
    async submit() {
      const isValidated = this.required(this.form)
      if (!isValidated) return

      this.isLoading = true
      const result = await firebase.signin(this.form.email, this.form.password)
        if (result && result.stat == false) {
          const { error } = useToast()
          error('Authentication failed', this.formatAuthError(result.value))
        } else {
          const { success } = useToast()
          success('Welcome back!', 'You have signed in successfully.')
          this.$router.push(this.$route.query.redirect || '/admin')
        }
      this.isLoading = false
    },
    async submitReset() {
      if (!this.validateResetEmail()) return

      this.isLoading = true
      const result = await firebase.resetPassword(this.resetEmail)
      const { success, error } = useToast()

      if (result?.stat) {
        success('Check your email', 'If an account exists, a password reset link has been sent.')
        this.closeResetMode()
        this.form.email = this.resetEmail
      } else {
        error('Reset failed', this.formatAuthError(result?.value))
      }
      this.isLoading = false
    },
  },
}
</script>
